// src/admin/components/owners/OwnerForm.jsx
import React, { useState, useEffect, useMemo } from 'react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  RiUserLine,
  RiSaveLine,
  RiCloseLine,
  RiArrowLeftLine,
  RiErrorWarningLine,
  RiBuildingLine,
  RiCheckboxCircleLine,
  RiCloseCircleLine,
  RiSearchLine,
  RiArrowDownSLine,
  RiLayoutMasonryLine
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  createOwner,
  getOwner,
  updateOwner,
  validateOwnerData,
  getOwnerProperties
} from '../../../models/ownerModel';


import {
  getAllApartments  // Add this import
} from '../../../models/apartmentModel';

const OwnerForm = ({ isEdit = false }) => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Available apartments (without owners)
  const [availableApartments, setAvailableApartments] = useState([]);
  const [defaultCountry, setDefaultCountry] = useState('iq'); // Fallback location

  // Owner data
  const [ownerData, setOwnerData] = useState({
    name: '',
    phone: '',
    livingThere: false,
    preferredLanguage: 'ku',
    apartments: []
  });

  // Selected apartment IDs
  const [selectedApartmentIds, setSelectedApartmentIds] = useState([]);

  // Showroom picker state
  const [showroomSearch, setShowroomSearch] = useState('');
  const [showroomTypeFilter, setShowroomTypeFilter] = useState('all');
  const [expandedZones, setExpandedZones] = useState(new Set());

  // Load available apartments and owner data if editing
  // Load available apartments and owner data if editing
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // Fetch location for default country code
        fetch('https://ipapi.co/json/')
          .then(res => res.json())
          .then(data => {
            if (data.country_code) setDefaultCountry(data.country_code.toLowerCase());
          })
          .catch(() => console.log('Location detection failed'));

        // Fetch apartments without owners using the correct API
        try {
          const response = await getAllApartments({ withoutOwner: 'true' });
          setAvailableApartments(response.apartments || []);
        } catch (err) {
          console.error('Error fetching apartments:', err);
          // If the API doesn't support withoutOwner filter, fetch all apartments
          const allApartmentsResponse = await getAllApartments();
          const allApartments = allApartmentsResponse.apartments || [];
          // Filter out apartments that already have owners
          const availableApartments = allApartments.filter(apt => !apt.ownerId);
          setAvailableApartments(availableApartments);
        }

        // If editing, load owner data
        if (isEdit && id) {
          const ownerResponse = await getOwner(id);

          if (ownerResponse.owner) {
            const owner = ownerResponse.owner;
            setOwnerData({
              name: owner.name || '',
              phone: owner.phone || '',
              livingThere: owner.livingThere || false,
              preferredLanguage: owner.preferredLanguage || 'ku'
            });

            // Get owner's current apartments
            const propertiesResponse = await getOwnerProperties(id);
            const currentApartments = propertiesResponse.apartments || [];

            // Set selected apartments
            setSelectedApartmentIds(currentApartments.map(a => a.id));

            // Add current apartments to available apartments list for editing
            if (currentApartments.length > 0) {
              setAvailableApartments(prev => {
                const existingIds = prev.map(apt => apt.id);
                const toAdd = currentApartments.filter(apt => !existingIds.includes(apt.id));
                return [...prev, ...toAdd];
              });
            }
          }
        }

      } catch (err) {
        console.error('Error loading data:', err);
        setError(t('failed_to_load_data') || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [isEdit, id, t]);


  // Toggle apartment selection
  const toggleApartmentSelection = (apartmentId) => {
    setSelectedApartmentIds(prev =>
      prev.includes(apartmentId) ? prev.filter(id => id !== apartmentId) : [...prev, apartmentId]
    );
  };

  // Group available apartments by floor (zone), sorted numerically
  const groupedByZone = useMemo(() => {
    const groups = {};
    availableApartments.forEach(apt => {
      const zone = apt.floor || 1;
      if (!groups[zone]) groups[zone] = [];
      groups[zone].push(apt);
    });
    Object.values(groups).forEach(arr =>
      arr.sort((a, b) => {
        const na = parseInt(a.apartmentNumber);
        const nb = parseInt(b.apartmentNumber);
        return isNaN(na) || isNaN(nb) ? a.apartmentNumber.localeCompare(b.apartmentNumber) : na - nb;
      })
    );
    return Object.keys(groups)
      .sort((a, b) => Number(a) - Number(b))
      .map(zk => ({ zone: zk, units: groups[zk] }));
  }, [availableApartments]);

  const distinctTypes = useMemo(() => {
    const s = new Set(availableApartments.map(a => a.type).filter(Boolean));
    return [...s].sort();
  }, [availableApartments]);

  const filteredGroups = useMemo(() => {
    const q = showroomSearch.trim().toLowerCase();
    return groupedByZone.map(({ zone, units }) => ({
      zone,
      units: units.filter(apt => {
        const matchSearch = !q ||
          String(apt.apartmentNumber).toLowerCase().includes(q) ||
          (apt.buildingName || '').toLowerCase().includes(q) ||
          String(apt.floor || '').toLowerCase().includes(q) ||
          (apt.type || '').toLowerCase().includes(q);
        const matchType = showroomTypeFilter === 'all' || apt.type === showroomTypeFilter;
        return matchSearch && matchType;
      })
    })).filter(g => g.units.length > 0);
  }, [groupedByZone, showroomSearch, showroomTypeFilter]);

  // Auto-expand first zone
  useEffect(() => {
    if (groupedByZone.length > 0 && expandedZones.size === 0) {
      setExpandedZones(new Set([groupedByZone[0].zone]));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedByZone]);

  const toggleZone = (zone) => {
    setExpandedZones(prev => {
      const n = new Set(prev);
      n.has(zone) ? n.delete(zone) : n.add(zone);
      return n;
    });
  };

  // Validate form
  const validateForm = () => {
    const validation = validateOwnerData(ownerData);

    if (!validation.isValid) {
      setError(Object.values(validation.errors)[0]);
      return false;
    }

    return true;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    try {
      setSaving(true);

      const payload = {
        ...ownerData,
        apartments: selectedApartmentIds
      };

      console.log('Submitting owner data:', payload); // Debug log

      if (isEdit) {
        await updateOwner(id, payload);
      } else {
        await createOwner(payload);
      }

      navigate('/admin/owners');

    } catch (err) {
      console.error('Error saving:', err);
      setError(err.message || t('failed_to_save_owner') || 'Failed to save owner');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`${isDarkMode ? 'bg-zinc-800' : 'bg-white'} rounded-xl border shadow-lg p-8 text-center`}>
        <div className="animate-spin h-8 w-8 border-4 border-red-500 border-t-transparent rounded-full mx-auto"></div>
        <p className={`mt-4 ${isDarkMode ? 'text-zinc-300' : 'text-zinc-600'}`}>{t('loading') || 'Loading...'}</p>
      </div>
    );
  }

  return (
    <div className={`${isDarkMode ? 'bg-zinc-800 border-zinc-700' : 'bg-white border-zinc-200'} rounded-xl border shadow-lg`}>
      {/* Header */}
      <div className={`p-5 border-b ${isDarkMode ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-zinc-50'}`}>
        <div className="flex justify-between items-center">
          <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-zinc-800'} flex items-center`}>
            <RiUserLine className={`mr-3 text-2xl ${isDarkMode ? 'text-red-400' : 'text-red-600'}`} />
            {isEdit ? t('edit_property_owner') || 'Edit Property Owner' : t('add_property_owner') || 'Add Property Owner'}
          </h2>
          <button
            onClick={() => navigate('/admin/owners')}
            className={`p-2 rounded-full ${isDarkMode ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-zinc-100 hover:bg-zinc-200'}`}
          >
            <RiArrowLeftLine size={22} />
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className={`mx-5 mt-5 p-4 rounded-lg ${isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'} border flex items-center`}>
          <RiErrorWarningLine className="mr-2 text-xl" />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="p-5">

        {/* OWNER INFORMATION */}
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-zinc-750' : 'bg-zinc-50'} mb-6`}>
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-zinc-900'} flex items-center`}>
            <RiUserLine className="mr-2" /> {t('owner_information') || 'Owner Information'}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                {t('name') || 'Name'} *
              </label>
              <input
                type="text"
                value={ownerData.name}
                onChange={(e) => setOwnerData({ ...ownerData, name: e.target.value })}
                className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-zinc-300'}`}
                required
              />
            </div>

            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                {t('phone') || 'Phone'} *
              </label>
              <div dir="ltr">
                <PhoneInput
                  country={defaultCountry}
                  value={ownerData.phone}
                  onChange={phone => setOwnerData({ ...ownerData, phone: '+' + phone })}
                  enableSearch={true}
                  disableSearchIcon={true}
                  buttonStyle={{
                    backgroundColor: isDarkMode ? '#27272a' : '#f9fafb',
                    borderColor: isDarkMode ? '#3f3f46' : '#d1d5db',
                  }}
                  inputStyle={{
                    width: '100%',
                    backgroundColor: isDarkMode ? '#27272a' : '#ffffff',
                    borderColor: isDarkMode ? '#3f3f46' : '#d1d5db',
                    color: isDarkMode ? '#ffffff' : '#111827',
                  }}
                  dropdownStyle={{
                    backgroundColor: isDarkMode ? '#27272a' : '#ffffff',
                    color: isDarkMode ? '#ffffff' : '#111827',
                  }}
                />
              </div>
            </div>

            <div>
              <label className={`block mb-1 text-sm font-medium ${isDarkMode ? 'text-zinc-300' : 'text-zinc-700'}`}>
                {t('preferred_language') || 'Preferred Language'} *
              </label>
              <select
                value={ownerData.preferredLanguage || 'en'}
                onChange={(e) => setOwnerData({ ...ownerData, preferredLanguage: e.target.value })}
                className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-zinc-300'}`}
                required
              >
                <option value="en">{t('english') || 'English'}</option>
                <option value="ar">{t('arabic') || 'Arabic'}</option>
                <option value="ku">{t('kurdish') || 'Kurdish'}</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className={`flex items-center gap-3 cursor-pointer p-3 rounded-lg border ${isDarkMode ? 'border-zinc-600 bg-zinc-700 hover:bg-zinc-600' : 'border-zinc-200 bg-white hover:bg-zinc-50'} transition-colors`}>
                <input
                  type="checkbox"
                  checked={ownerData.livingThere || false}
                  onChange={(e) => setOwnerData({ ...ownerData, livingThere: e.target.checked })}
                  className="w-4 h-4 accent-red-600"
                />
                <span className={`text-sm font-medium ${isDarkMode ? 'text-zinc-200' : 'text-zinc-700'}`}>
                  {t('lives_on_property') || 'Lives on the property'}
                </span>
                {ownerData.livingThere && (
                  <span className={`ml-auto text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-700'}`}>
                    {t('monthly_service_fee_applies') || 'Monthly service fee applies'}
                  </span>
                )}
              </label>
            </div>
          </div>
        </div>

        {/* SELECT APARTMENTS */}
        <div className={`p-5 rounded-xl border mb-5 ${isDarkMode ? 'bg-zinc-800/50 border-zinc-700' : 'bg-zinc-50/50 border-zinc-200'}`}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={`text-base font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
              <RiBuildingLine className="text-red-500" />
              {t('select_apartments', { count: selectedApartmentIds.length }) || `Select Showrooms (${selectedApartmentIds.length} selected)`}
            </h3>
            <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>
              {t('available_count', { count: availableApartments.length }) || `${availableApartments.length} available`}
            </span>
          </div>

          {availableApartments.length === 0 ? (
            <div className={`py-8 text-center rounded-lg ${isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
              <RiBuildingLine size={32} className="mx-auto mb-2 text-zinc-400" />
              <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>
                {t('no_apartments_available') || 'No apartments available. All apartments already have owners.'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Selected units badges */}
              {selectedApartmentIds.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {selectedApartmentIds.map(sid => {
                    const apt = availableApartments.find(a => a.id === sid);
                    if (!apt) return null;
                    return (
                      <div key={sid} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs ${isDarkMode ? 'bg-red-900/20 border-red-700/50 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
                        <RiCheckboxCircleLine size={13} className="text-red-500" />
                        <span className="font-semibold">#{apt.apartmentNumber}</span>
                        <span className={`${isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}`}>{t('zone', 'Zone')} {apt.floor}</span>
                        <button
                          type="button"
                          onClick={() => toggleApartmentSelection(sid)}
                          className={`ml-1 ${isDarkMode ? 'text-zinc-500 hover:text-white' : 'text-zinc-400 hover:text-zinc-700'}`}
                        >
                          ×
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Search + Type filter */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <RiSearchLine size={15} className={`absolute top-1/2 -translate-y-1/2 start-3 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`} />
                  <input
                    type="text"
                    value={showroomSearch}
                    onChange={e => setShowroomSearch(e.target.value)}
                    placeholder={t('search_by_unit', 'Search by unit #, zone, type…')}
                    className={`w-full ps-9 pe-3 py-2 text-sm rounded-lg border outline-none focus:ring-2 focus:ring-red-500/40 ${isDarkMode ? 'bg-zinc-700 border-zinc-600 text-white placeholder:text-zinc-500' : 'bg-white border-zinc-300 text-zinc-900 placeholder:text-zinc-400'}`}
                  />
                </div>
                <select
                  value={showroomTypeFilter}
                  onChange={e => setShowroomTypeFilter(e.target.value)}
                  className={`text-sm px-3 py-2 rounded-lg border outline-none focus:ring-2 focus:ring-red-500/40 ${isDarkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-zinc-300 text-zinc-900'}`}
                >
                  <option value="all">{t('all_types', 'All Types')}</option>
                  {distinctTypes.map(tp => <option key={tp} value={tp}>{tp}</option>)}
                </select>
              </div>

              {/* Zone accordion */}
              {filteredGroups.length === 0 ? (
                <div className={`py-6 text-center rounded-lg border-2 border-dashed ${isDarkMode ? 'border-zinc-700 text-zinc-500' : 'border-zinc-200 text-zinc-400'}`}>
                  <p className="text-sm">{t('no_showrooms_match', 'No showrooms match your search.')}</p>
                  <button type="button" onClick={() => { setShowroomSearch(''); setShowroomTypeFilter('all'); }} className="mt-2 text-xs text-red-500 hover:underline">
                    {t('clear_filters', 'Clear filters')}
                  </button>
                </div>
              ) : (
                <div className="space-y-2 max-h-80 overflow-y-auto pe-1">
                  {filteredGroups.map(({ zone, units }) => {
                    const isExpanded = expandedZones.has(zone);
                    const vacantCt = units.filter(a => a.status === 'vacant').length;
                    return (
                      <div key={zone} className={`rounded-xl border overflow-hidden transition-shadow ${isDarkMode ? 'bg-zinc-800/60 border-zinc-700' : 'bg-white border-zinc-200'} ${isExpanded ? 'shadow-md' : 'shadow-sm hover:shadow-md'}`}>
                        {/* Zone header */}
                        <button
                          type="button"
                          onClick={() => toggleZone(zone)}
                          className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${isDarkMode ? 'hover:bg-zinc-700/50' : 'hover:bg-zinc-50'} ${isExpanded ? `border-b ${isDarkMode ? 'border-zinc-700' : 'border-zinc-100'}` : ''}`}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg font-bold text-xs shrink-0 ${isDarkMode ? 'bg-zinc-700 border border-zinc-600 text-zinc-300' : 'bg-zinc-100 border border-zinc-200 text-zinc-700'}`}>
                              <RiLayoutMasonryLine className={isDarkMode ? 'text-red-400' : 'text-red-500'} size={13} />
                              {t('zone', 'ZONE')} {zone}
                            </div>
                            <span className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{units.length} {t('units', 'units')}</span>
                            {vacantCt > 0 && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-500/10 text-green-400 border border-green-500/20">
                                {vacantCt} Vacant
                              </span>
                            )}
                          </div>
                          <RiArrowDownSLine size={18} className={`transition-transform duration-250 ${isExpanded ? 'rotate-180' : ''} ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`} />
                        </button>

                        {/* Zone units */}
                        {isExpanded && (() => {
                          const sorted = [...units].sort((a, b) => {
                            const na = parseInt(a.apartmentNumber), nb = parseInt(b.apartmentNumber);
                            return isNaN(na) || isNaN(nb) ? a.apartmentNumber.localeCompare(b.apartmentNumber) : na - nb;
                          });
                          const hasPosData = sorted.some(a => a.rowPosition);
                          let frontRow, backRow, cornerUnits;
                          if (hasPosData) {
                            frontRow    = sorted.filter(a => a.rowPosition === 'front');
                            backRow     = [...sorted.filter(a => a.rowPosition === 'back')].reverse();
                            cornerUnits = sorted.filter(a => a.rowPosition === 'corner');
                          } else {
                            const half = Math.floor(sorted.length / 2);
                            frontRow    = sorted.slice(0, half);
                            backRow     = [...sorted.slice(half)].reverse();
                            cornerUnits = [];
                          }

                          const UnitCard = ({ apt }) => {
                            const isSelected = selectedApartmentIds.includes(apt.id);
                            const statusColor = apt.status === 'vacant' ? '#3b82f6' : '#f59e0b';
                            return (
                              <div
                                onClick={() => toggleApartmentSelection(apt.id)}
                                className={`relative flex flex-col rounded-xl border-2 overflow-hidden cursor-pointer select-none transition-all hover:-translate-y-1 shrink-0 w-[110px]
                                  ${isSelected
                                    ? isDarkMode ? 'border-red-500 bg-red-900/20 shadow-lg shadow-red-900/30' : 'border-red-500 bg-red-50'
                                    : isDarkMode ? 'border-zinc-700 bg-zinc-800 hover:border-zinc-500 hover:shadow-lg' : 'border-zinc-200 bg-white hover:border-zinc-300 hover:shadow-md'
                                  }`}
                              >
                                <div className="absolute inset-x-0 top-0 h-1 rounded-t-xl" style={{ backgroundColor: isSelected ? '#ef4444' : statusColor }} />
                                <div className="p-2.5 pt-3.5 flex flex-col gap-1">
                                  <div className="flex items-start justify-between">
                                    <span className={`font-mono font-black text-lg leading-none ${isDarkMode ? 'text-white' : 'text-zinc-900'}`}>
                                      {apt.apartmentNumber}
                                    </span>
                                    {isSelected
                                      ? <RiCheckboxCircleLine className="text-red-500 shrink-0" size={15} />
                                      : <span className="text-[8px] font-bold uppercase px-1 py-0.5 rounded"
                                          style={{ backgroundColor: `${statusColor}20`, color: statusColor, border: `1px solid ${statusColor}30` }}>
                                          {t(apt.status, apt.status)}
                                        </span>
                                    }
                                  </div>
                                  {apt.type && (
                                    <span className={`self-start text-[8px] font-bold uppercase px-1.5 py-0.5 rounded border ${isDarkMode ? 'bg-blue-500/20 text-blue-300 border-blue-500/40' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>
                                      {t(`type_${apt.type.toLowerCase().replace(/\\s+/g, '_')}`, apt.type)}
                                    </span>
                                  )}
                                  {apt.size && (
                                    <p className={`text-[9px] mt-0.5 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-400'}`}>{apt.size} m²</p>
                                  )}
                                </div>
                              </div>
                            );
                          };

                          return (
                            <div className={`p-3 ${isDarkMode ? 'bg-zinc-900/30' : 'bg-zinc-50/50'}`}>
                              <div className="overflow-x-auto">
                                <div className={`inline-flex gap-3 p-3 rounded-2xl border min-w-max ${isDarkMode ? 'bg-zinc-900/60 border-zinc-700/60' : 'bg-zinc-50/80 border-zinc-200'}`}>
                                  <div className="flex flex-col gap-2">
                                    {/* ↑ BACK ROW */}
                                    <div className="flex flex-col gap-1.5">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-zinc-200 text-zinc-500'}`}>↑ {t('back_row', 'Back Row')}</span>
                                      </div>
                                      <div className={`flex gap-2 px-3 py-2 rounded-xl border border-dashed ${isDarkMode ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-300 bg-white/60'}`}>
                                        {backRow.map(apt => <UnitCard key={apt.id} apt={apt} />)}
                                      </div>
                                    </div>
                                    {/* Wall divider */}
                                    <div className="flex items-center gap-2 px-2">
                                      <div className={`flex-1 h-2 rounded-full ${isDarkMode ? 'bg-zinc-600' : 'bg-zinc-300'}`} />
                                      <span className={`text-[9px] font-bold uppercase tracking-widest shrink-0 ${isDarkMode ? 'text-zinc-600' : 'text-zinc-400'}`}>─ {t('wall', 'wall')} ─</span>
                                      <div className={`flex-1 h-2 rounded-full ${isDarkMode ? 'bg-zinc-600' : 'bg-zinc-300'}`} />
                                    </div>
                                    {/* ↓ FRONT ROW */}
                                    <div className="flex flex-col gap-1.5">
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-zinc-200 text-zinc-500'}`}>↓ {t('front_row', 'Front Row')}</span>
                                      </div>
                                      <div className={`flex gap-2 px-3 py-2 rounded-xl border border-dashed ${isDarkMode ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-300 bg-white/60'}`}>
                                        {frontRow.map(apt => <UnitCard key={apt.id} apt={apt} />)}
                                      </div>
                                    </div>
                                  </div>
                                  {/* Corner column */}
                                  {cornerUnits.length > 0 && (
                                    <div className={`flex flex-col gap-2 justify-center ps-3 border-s ${isDarkMode ? 'border-zinc-700' : 'border-zinc-300'}`}>
                                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded ${isDarkMode ? 'bg-zinc-700 text-zinc-400' : 'bg-zinc-200 text-zinc-500'}`}>{t('corner', 'Corner')}</span>
                                      <div className={`flex flex-col gap-2 px-2 py-2 rounded-xl border border-dashed ${isDarkMode ? 'border-zinc-700 bg-zinc-800/30' : 'border-zinc-300 bg-white/60'}`}>
                                        {cornerUnits.map(apt => <UnitCard key={apt.id} apt={apt} />)}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Submit Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/admin/owners')}
            className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-700 hover:bg-zinc-600' : 'bg-zinc-200 hover:bg-zinc-300'} transition-colors`}
            disabled={saving}
          >
            <RiCloseLine className="mr-1 inline" size={18} />
            {t('cancel') || 'Cancel'}
          </button>

          <button
            type="submit"
            className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-600 hover:bg-red-700' : 'bg-red-600 hover:bg-red-700'} text-white transition-colors flex items-center`}
            disabled={saving}
          >
            {saving ? (
              <>
                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                {t('saving') || 'Saving...'}
              </>
            ) : (
              <>
                <RiSaveLine className="mr-2" size={18} />
                {isEdit ? t('update_owner') || 'Update Owner' : t('create_owner') || 'Create Owner'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OwnerForm;